import {
  type PageProfileInput,
  type PageProfileOutline,
  LAYOUT_PATTERN_DESCRIPTIONS,
} from '../types/page-profile';

/**
 * Build the prompt for Stage 1: PLAN
 * Takes PageProfileInput and produces a PageProfileOutline JSON
 */
export const buildPageProfilePlanPrompt = (
  input: PageProfileInput,
  isRetry: boolean,
): { system: string; user: string } => {
  const layoutPatternDocs = Object.entries(LAYOUT_PATTERN_DESCRIPTIONS)
    .map(([pattern, description]) => `- \`${pattern}\`: ${description}`)
    .join('\n');

  const system = `You are a landing page strategist. Analyze the provided product data and produce a comprehensive PageProfileOutline as JSON.

## Output Format

Output ONLY valid JSON with no additional text, explanation, or markdown formatting. Do not wrap the JSON in code blocks (\`\`\`). Return raw JSON matching this schema:

{
  "businessType": "specific business type description",
  "industry": "industry category",
  "tone": "professional | playful | luxury | bold | minimal | warm | technical | friendly",
  "pagePurpose": "lead-generation | product-showcase | service-overview | event-promotion | portfolio | landing-page",
  "targetAudience": "specific audience based on product data",
  "valueProposition": "core benefit in one compelling sentence",
  "colorDirection": "evocative color guidance for style generation",
  "designDirectives": {
    "spacingScale": "tight | balanced | airy",
    "borderRadiusScale": "sharp | subtle | rounded",
    "shadowUsage": "none | subtle | dramatic",
    "layoutDensity": "compact | standard | spacious"
  },
  "animationStrategy": {
    "sectionEntry": "fadeIn | slideUp | blurIn | revealUp | zoomIn",
    "cardHover": "scale | lift | glow | none",
    "imageHover": "scale | brighten | none",
    "imageContinuous": "kenBurns | slowZoom | none"
  },
  "pageConfig": {
    "backgroundColor": "#hex color for page background",
    "maxWidth": "1200px",
    "padding": "20,20,20,20"
  },
  "sections": [
    {
      "type": "hero | features | benefits | social-proof | cta | pricing | faq | team | gallery | contact | stats | process | comparison | logo-bar | newsletter | video | quote | about | services | portfolio | awards",
      "purpose": "what this section accomplishes for the visitor",
      "layoutPattern": "single-column | two-column-left | two-column-right | three-column | three-column-with-header | four-column | four-column-with-header | grid-2x2 | grid-3 | alternating | asymmetric-left | asymmetric-right | stacked-hero | split-screen | cards-row | zigzag | hero-image-left | hero-image-right | hero-full-image | cta-image-bg",
      "spacingIntensity": "tight | balanced | airy",
      "visualStyle": {
        "backgroundType": "transparent | light | dark | accent | gradient",
        "backgroundHint": "#hex, gradient description, or 'none' for transparent"
      },
      "contentDirection": {
        "headline": "suggested headline 3-8 words",
        "subheadlineTheme": "what the subheadline communicates",
        "bodyTheme": "what body copy should cover",
        "ctaText": "suggested CTA text if applicable"
      },
      "assignedImages": [
        {
          "imageId": "img_0",
          "role": "hero | feature | background | accent | testimonial | product",
          "fit": "cover | contain"
        }
      ],
      "assignedTestimonials": [
        {
          "testimonialId": "review_0"
        }
      ]
    }
  ]
}

## Image Assignment Rules

You MUST assign images from the Available Images list to appropriate sections:

1. **Hero sections**: Assign images with "hero-image" trait or high-quality lifestyle/model images. If no suitable image exists, leave assignedImages empty (a placeholder will be used).
2. **Features/Benefits sections**: Assign product images or screenshots. Can use multiple images.
3. **Social-proof sections**: Assign images with "testimonial" trait or model/lifestyle images for customer photos.
4. **Gallery sections**: Assign multiple product or lifestyle images.
5. **About/Team sections**: Assign model or lifestyle images.
6. **Other sections**: Assign relevant images or leave empty.

**Image fit rules:**
- Use fit="contain" for images with hasTransparency=true (product images on transparent background)
- Use fit="cover" for photos, lifestyle images, and backgrounds

**Background image selection — hasText restriction:**
Some images in the list below are marked "hasText: true". These images have words, logos, or typographic content burned into the image itself (for example, a gym banner that says "STRENGTH" or a promotional graphic with "Studio Interior" text). Here is why this matters and what you must do:

- When you place text content on TOP of a background image (which is what happens with hero-full-image, hero-image-left, hero-image-right, and cta-image-bg layouts), the text you write overlaps with the text already baked into the image. The result is two layers of competing text that is unreadable and looks broken. This is the single most common quality issue we see in generated pages.
- Therefore: for any section that uses an image-background layout pattern (hero-full-image, hero-image-left, hero-image-right, cta-image-bg), you must ONLY assign images that do NOT have "hasText: true". Check each image's metadata before assigning it.
- If every available image has hasText=true and no clean image exists, leave assignedImages empty for that section. The section generator will use a solid color or gradient background instead, which looks much better than text-on-text.
- Images with hasText=true are perfectly fine for inline usage — assign them to features, benefits, gallery, or product sections where they appear as standalone <image> elements and no text is placed on top of them.

**IMPORTANT**:
- Only assign images that have traits/metadata - images without traits are low quality
- Images CAN be reused across sections if appropriate
- Reference images by their imageId (e.g., "img_0", "img_1")

## Testimonial Assignment Rules

Assign testimonials to social-proof or quote sections:

1. **Social-proof sections**: Assign 2-3 testimonials with highest ratings
2. **Quote sections**: Assign 1 featured testimonial (longest/most detailed)
3. Reference testimonials by their testimonialId (e.g., "review_0", "review_1")

## Layout Patterns

Choose from these layout patterns based on section content needs:

${layoutPatternDocs}

## Rules

1. Plan 4-6 sections following landing page best practices
2. First section MUST be type "hero"
3. Last section should be "cta" or "contact" or "newsletter"
4. Exactly one section on the page should include a lead capture form (type "contact" or "newsletter"). This is a lead generation platform — every page needs a form to capture visitor information, but having multiple forms on the same page splits the visitor's attention and hurts conversion. Pick the single best placement for the form and keep all other sections form-free.
5. No two consecutive sections should have the same layoutPattern
6. ALTERNATE section backgrounds for visual rhythm:
   - Most sections (50%) should use backgroundType="transparent" to let the page background show through
   - Include 1-2 contrasting sections for drama
   - Include 1-2 complimentary sections for subtle variety
   - Never have 3+ consecutive sections with the same backgroundType
   - Use accent/gradient for special emphasis (CTAs, pricing)
7. contentDirection must be specific to this product, not generic placeholders
8. Consider the price tier and product type when setting tone:
   - Premium/luxury: elegant, sophisticated, generous whitespace
   - Mid-tier: balanced, approachable, professional
   - Budget: direct, value-focused, efficient
9. Use diverse layout patterns - avoid repetition, mix asymmetric, grid, and column layouts
10. PRIORITIZE IMAGE BACKGROUND LAYOUTS when suitable images are available:
   - Use hero-full-image, hero-image-left, or hero-image-right for hero sections (these are visually striking)
   - Use cta-image-bg for CTA sections (creates urgency and drama)
   - These layouts make the page feel premium and professionally designed
   - Aim for at least 2-3 sections with image backgrounds per page
   - Sections with image background layouts (hero-full-image, hero-image-left, hero-image-right, cta-image-bg) must use backgroundType="dark" with a dark backgroundHint. A dark overlay behind the image with contrasting text from the global palette ensures readability.

## Section Type Guidance

### Core Sections
- hero: Main value proposition, primary CTA. Use hero-full-image, hero-image-left, hero-image-right, two-column-left, two-column-right, stacked-hero, or split-screen. Image backgrounds create impact.
- features: Product capabilities. Use three-column-with-header, four-column-with-header, grid-3, or cards-row. Use "-with-header" variants when you need a section title.
- benefits: Customer outcomes. Use zigzag, two-column-left/right, or asymmetric layouts.
- social-proof: Multiple testimonials/reviews. Use three-column-with-header, cards-row, or grid-2x2.
- cta: Conversion action. Use cta-image-bg for dramatic effect, or single-column, split-screen with bold styling. CTA sections can include a short lead capture form (full name + email + submit) alongside the headline and button for direct conversion.
- pricing: Plan comparison. Use three-column-with-header or four-column-with-header for pricing cards.
- faq: Common questions. Use single-column or two-column-left layout.

### Content Sections
- about: Company/brand story. Use hero-image-left, hero-image-right, two-column-left, asymmetric-left, or stacked-hero.
- services: Service offerings list. Use three-column-with-header, four-column-with-header, or zigzag.
- process: Step-by-step flow. Use four-column (no header) or four-column-with-header for numbered steps. Also three-column or zigzag.
- gallery: Product showcase. Use grid-2x2, grid-3, or cards-row.

### Social & Trust Sections
- quote: Single featured testimonial. Use single-column or asymmetric-left with large quote styling.
- logo-bar: Partner/client logos. Use four-column (no header) for clean logo row, or cards-row.
- stats: Key metrics/numbers. Use three-column, four-column (no header), or grid-2x2.

### Layout Pattern Notes
- Use three-column or four-column (without "-with-header") when you DON'T need a section title—just the columns.
- Use three-column-with-header or four-column-with-header when the section NEEDS a title/subtitle above the columns.
- **PREFER IMAGE BACKGROUND LAYOUTS** - hero-image-left, hero-image-right, hero-full-image, and cta-image-bg create professional, visually striking pages. Use these whenever suitable images are available.
- Image background layouts work especially well for: hero sections, about sections, CTA sections, and any section where you want visual impact.

## Content Direction Guidelines

For contentDirection, write specific suggestions based on the product data:
- headline: State the core benefit in 3-8 words (e.g., "Handcrafted Silk That Tells Stories")
- subheadlineTheme: What supporting message to convey (e.g., "artisan craftsmanship, heritage, limited availability")
- bodyTheme: What details to expand on (e.g., "pure materials, collaboration with artisans, care instructions")
- ctaText: Action-specific text (e.g., "Pre-Order Now", "Shop Collection", "Book Consultation")

## Animation Strategy

Every page MUST have an animationStrategy. Choose ONE consistent animation theme for the entire page based on the tone and industry:

### sectionEntry — How sections animate into view on scroll
- \`fadeIn\`: Universal, works for any tone. Clean and professional.
- \`slideUp\`: Modern and dynamic. Great for tech, SaaS, e-commerce.
- \`blurIn\`: Dramatic and cinematic. Best for luxury, photography, creative brands.
- \`revealUp\`: Bold and editorial. Works for fashion, design, portfolio pages.
- \`zoomIn\`: Energetic and playful. Good for events, food, entertainment.

### cardHover — Hover effect on feature/benefit/testimonial cards
- \`lift\`: Cards lift with shadow. Professional, works for most pages.
- \`scale\`: Cards scale up slightly. Playful, interactive feel.
- \`glow\`: Cards get a glow effect. Techy, futuristic feel.
- \`none\`: No hover effect. Use for minimal/editorial designs.

### imageHover — Hover effect on inline images (NOT all images — only selected ones)
- \`scale\`: Subtle zoom on hover. Great for galleries, product images.
- \`brighten\`: Brightens on hover. Good for dark-themed pages.
- \`none\`: No image hover. Use for editorial or minimal designs.

### imageContinuous — Slow continuous animation on hero/background images
- \`kenBurns\`: Slow pan and zoom. Cinematic, great for hero images with real photos.
- \`slowZoom\`: Gradual zoom in. Subtle, works for product showcases.
- \`none\`: No continuous animation. Use when images are informational or have text overlay.

**Matching guide by tone:**
- **professional/minimal**: fadeIn + lift + none + none
- **playful/friendly**: slideUp + scale + scale + none
- **luxury**: blurIn + lift + brighten + kenBurns
- **bold**: revealUp + glow + scale + slowZoom
- **warm**: slideUp + lift + scale + kenBurns
- **technical**: fadeIn + glow + none + none

These are starting points — adjust based on the specific product and imagery available.

## Urgency and Countdown Timers

Countdown timers are a powerful conversion tool, but they should only be used when the product data contains EXPLICIT time-sensitive signals. Do not invent urgency where none exists.

Include urgency in the CTA section's purpose ONLY when the product description or title explicitly mentions one of these: a sale with an end date, a limited-time offer, a product launch date, an event with a registration deadline, an expiring discount or promotion, or language like "limited edition", "ending soon", "for a limited time", "early bird".

If the product is a general service, ongoing business, or evergreen product without any time pressure in the data, do NOT add urgency language to the section purpose. The section generator uses the purpose field to decide whether to add a countdown timer, so only mention urgency there when the product data genuinely warrants it. Most pages will NOT need a countdown.

${isRetry ? '\nCRITICAL: You MUST respond with ONLY valid JSON. No conversational text, no markdown fences.' : ''}`;

  const user = buildUserPrompt(input);

  return { system, user };
};

/**
 * Build the user prompt from PageProfileInput
 */
const buildUserPrompt = (input: PageProfileInput): string => {
  // Format brand colors for context
  const formatBrandColors = (): string => {
    if (!input.brandKit) return 'No colors extracted';

    const colors: string[] = [];
    if (input.brandKit.white) {
      colors.push(
        `- ${input.brandKit.white.hex} (white: ${input.brandKit.white.components?.join(', ') || 'general'})`,
      );
    }
    if (input.brandKit.black) {
      colors.push(
        `- ${input.brandKit.black.hex} (black: ${input.brandKit.black.components?.join(', ') || 'general'})`,
      );
    }
    input.brandKit.grays?.forEach((c) => {
      colors.push(`- ${c.hex} (gray: ${c.components?.join(', ') || 'general'})`);
    });
    input.brandKit.colors?.forEach((c) => {
      colors.push(`- ${c.hex} (${c.components?.join(', ') || 'accent'})`);
    });
    return colors.length > 0 ? colors.join('\n') : 'No colors extracted';
  };

  // Format images with IDs for assignment
  const imagesList = input.images
    .map((img, index) => {
      const meta = img.metadata;
      const id = `img_${index}`;
      const traits = meta?.traits?.join(', ') || 'none';
      const desc = meta?.description?.slice(0, 60) || 'No description';
      const dims = meta?.width && meta?.height ? `${meta.width}x${meta.height}` : 'unknown size';
      const transparency = meta?.hasTransparency ? ', transparent' : '';
      const luminescence =
        meta?.luminance !== undefined ? `, luminescence: ${meta.luminance.toFixed(2)}` : '';
      const brightness = meta?.brightness !== undefined ? `, brightness: ${meta.brightness}` : '';
      const hasTextLabel = meta?.hasText ? ' WARNING: CONTAINS TEXT' : '';
      return `- ${id}:${hasTextLabel} [${meta?.category || 'unknown'}/${meta?.purpose || 'unknown'}] ${desc}
    traits: ${traits}, ${dims}${transparency}${luminescence}${brightness}${meta?.hasText ? ', hasText: true (DO NOT use as background image)' : ''}`;
    })
    .join('\n');

  // Format reviews with IDs for assignment
  const reviewsList = (input.testimonials?.reviews || []).map((r, index) => {
    const id = `review_${index}`;
    const text = r.text.length > 150 ? r.text.slice(0, 150) + '...' : r.text;
    return `- ${id}: ${r.rating}/5 by ${r.author}
    "${text}"`;
  });

  // Format audience info
  const formatAudience = (): string => {
    if (!input.audience) return 'No audience data';

    const parts: string[] = [];
    if (input.audience.description) {
      parts.push(`Description: ${input.audience.description.slice(0, 300)}...`);
    }
    if (input.audience.ageRange) {
      parts.push(
        `Age Range: ${input.audience.ageRange.min || '?'}-${input.audience.ageRange.max || '?'}`,
      );
    }
    if (input.audience.incomeLevel) {
      parts.push(`Income: ${input.audience.incomeLevel}`);
    }
    if (input.audience.interests?.length) {
      parts.push(`Interests: ${input.audience.interests.slice(0, 5).join(', ')}`);
    }
    if (input.audience.painPoints?.length) {
      parts.push(`Pain Points: ${input.audience.painPoints.slice(0, 5).join(', ')}`);
    }
    return parts.join('\n');
  };

  // Format price info
  const priceInfo = input.categorization?.price
    ? `${input.categorization.currency || 'USD'} ${input.categorization.price}`
    : 'Not specified';
  const priceTier = input.categorization?.priceTier || 'Not specified';

  return `## Business/Product Data

Title: ${input.title}
Industry: ${input.categorization?.industry || 'Unknown'}
Category: ${input.categorization?.category || 'Unknown'}
Subcategory: ${input.categorization?.subcategory || 'Unknown'}

## Pricing
Price: ${priceInfo}
Price Tier: ${priceTier}

## Description
${input.description}

## Target Audience
${formatAudience()}

## Brand Colors
${formatBrandColors()}

## Available Images (${input.images.length} total)

IMPORTANT: Use imageId values (img_0, img_1, etc.) when assigning images to sections.

${imagesList || 'No images provided'}

## Customer Reviews (${input.testimonials?.reviews?.length || 0} total)

IMPORTANT: Use testimonialId values (review_0, review_1, etc.) when assigning testimonials to sections.

${reviewsList.length > 0 ? reviewsList.join('\n\n') : 'No reviews provided'}
${input.testimonials?.note ? `\nNote: ${input.testimonials.note}` : ''}

Analyze this data and return the PageProfileOutline JSON. Make creative decisions based on the data — do not ask for clarification.`;
};

/**
 * Try to parse the LLM response as a PageProfileOutline JSON.
 * Returns the outline on success, or null on parse failure.
 */
export const tryParsePageProfileOutline = (responseText: string): PageProfileOutline | null => {
  try {
    // Try to extract JSON from the response
    let jsonText = responseText.trim();

    // Strip markdown code fences if present
    const codeBlockMatch = jsonText.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (codeBlockMatch) {
      jsonText = codeBlockMatch[1].trim();
    }

    // Find JSON object boundaries
    const firstBrace = jsonText.indexOf('{');
    const lastBrace = jsonText.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      jsonText = jsonText.slice(firstBrace, lastBrace + 1);
    }

    return JSON.parse(jsonText) as PageProfileOutline;
  } catch {
    return null;
  }
};

/**
 * Validate the parsed PageProfileOutline has required fields
 */
export const validatePageProfileOutline = (outline: PageProfileOutline): void => {
  if (!outline.businessType) {
    throw new Error('PageProfileOutline missing businessType');
  }
  if (!outline.sections || outline.sections.length === 0) {
    throw new Error('PageProfileOutline missing sections');
  }
  if (!outline.designDirectives) {
    throw new Error('PageProfileOutline missing designDirectives');
  }
  if (!outline.valueProposition) {
    throw new Error('PageProfileOutline missing valueProposition');
  }
  if (!outline.pageConfig) {
    // Provide defaults if missing
    outline.pageConfig = {
      backgroundColor: '#f8f7f4',
      maxWidth: '1200px',
      padding: '20,20,20,20',
    };
  }

  if (!outline.animationStrategy) {
    // Provide sensible defaults if LLM omitted animation strategy
    outline.animationStrategy = {
      sectionEntry: 'fadeIn',
      cardHover: 'lift',
      imageHover: 'none',
      imageContinuous: 'none',
    };
  }

  // Ensure each section has required fields
  for (const section of outline.sections) {
    if (!section.type) {
      throw new Error('Section missing type');
    }
    if (!section.contentDirection) {
      throw new Error(`Section ${section.type} missing contentDirection`);
    }
    // Initialize assignedImages array if not present
    if (!section.assignedImages) {
      section.assignedImages = [];
    }
  }
};
